import FullComposeDroppableContainer from './FullComposeDroppableContainer';
import FullComposeHeader from './FullComposeHeader';
import FullComposeInfoBar from './FullComposeInfoBar';
import FullComposeLeftPane, { FullComposeLeftPaneHandle } from './FullComposeLeftPane';
import FullComposeRightPane from './FullComposeRightPane';
import FullComposeSchedulingAssistant from './FullComposeSchedulingAssistant';
import FullComposeSidePane from './SidePanes/FullComposeSidePane';
import MeetingResponseDialogContainer from './MeetingResponseDialogContainer';
import getComposeCommandBarItems from '../utils/getComposeCommandBarItems';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { observer } from 'mobx-react-lite';
import { isConnectedAccount } from 'owa-accounts-store';
import {
    lazyShowAdminInstalledNewAddinFRECallout,
    lazyShowUnpinnedAdminAddinFRECallout,
} from 'owa-addins-exp-ui';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { createAppointmentComposeAdapter } from 'owa-calendar-addins';
import initializeMailTips from 'owa-calendar-compose-form-actions/lib/actions/initializeMailTips';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import {
    getComposeEvent,
    getOriginalEvent,
    getFullComposeViewState,
} from 'owa-calendar-compose-store-manager';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { FormsCommandBar, CALENDAR_ELLIPSES_ID } from 'owa-calendar-forms-command-bar';
import { getCommands } from 'owa-calendar-hotkeys';
import { lazyInitRoomPreferencesSetting } from 'owa-calendar-room-preferences-option';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { useGlobalHotkey } from 'owa-hotkeys';
import { getOpxHostApi } from 'owa-opx';
import LaunchEventType from 'owa-service/lib/contract/LaunchEventType';
import * as React from 'react';
import { updateSidePaneType, verifyEventThenSave } from 'owa-calendar-compose-form-actions';
import {
    lazyInitializeAddinsForItem,
    lazyOnLaunchEventTriggered,
    getComposeHostItemIndex,
    lazyDeinitializeAddinsForItem,
    lazyOnComposeClose,
    getSurfaceNewAddinId,
    getExtensionForAddinId,
} from 'owa-addins-core';
import CalendarBarButtons from 'owa-calendar-forms-common/lib/components/CalendarBarButtons';
import { getButtonProps } from '../utils/getFullComposeButtons';
import { shouldShowFormHeader } from '../utils/shouldShowFormHeader';

const styles = require('./FullComposeForm.scss');
const classNames = require('classnames/bind').bind(styles);

export interface FullComposeFormProps {
    formId?: string | null;
}

export default observer(function FullComposeForm(props: FullComposeFormProps) {
    const composeFormId = props.formId || null;

    const event = getComposeEvent(composeFormId);
    const originalEvent = getOriginalEvent(composeFormId);
    const fullComposeViewState = getFullComposeViewState(composeFormId);

    const calendarColorScheme: ColorScheme = getComposeCalendarColorScheme(composeFormId);

    const leftPane = React.useRef<FullComposeLeftPaneHandle>();

    const hostItemIndex = event && getComposeHostItemIndex(event.ParentFolderId.Id);
    const showFullScreen =
        isInPopOutWindow() || isHostAppFeatureEnabled('fullscreenComposeFormPopOut');

    React.useEffect(() => {
        // Send message in opx scenarios that the component is loaded
        if (isHostAppFeatureEnabled('opxComponentLifecycle')) {
            getOpxHostApi().sendPLTDataPoint?.('ComposeForm');
        }
    }, []);

    React.useEffect((): void | (() => void) => {
        const adapter = createAppointmentComposeAdapter();

        if (adapter) {
            const addinSetupPromise = lazyInitializeAddinsForItem
                .import()
                .then(initializeAddinsForItem => {
                    initializeAddinsForItem(
                        hostItemIndex,
                        adapter,
                        false /* renderTaskPaneInPanel */
                    ).then(
                        // trigger lazyOnLaunchEventTriggered action once AppointmentComposeAdapter is created and addins are initialized.
                        () => {
                            const eventFromStore = getComposeEvent(composeFormId);
                            if (
                                isFeatureEnabled('addin-autoRun') &&
                                isFeatureEnabled('addin-autoRun-newComposeEvent') &&
                                eventFromStore &&
                                !eventFromStore.ItemId?.Id
                            ) {
                                lazyOnLaunchEventTriggered.importAndExecute(
                                    hostItemIndex,
                                    LaunchEventType.OnNewAppointmentOrganizer
                                );
                            }

                            if (isFeatureEnabled('addin-exp-pinNewAdminAddinsFRECallout')) {
                                // One of the treatment in experiment to pin newly installed admin addins
                                // and show lightning nudge for latest addin.
                                window.requestAnimationFrame(() => {
                                    showLatestNewAdminAddinFRECallout();
                                });
                            }

                            if (
                                isFeatureEnabled('addin-exp-awarenessNudgeCusBtn') ||
                                isFeatureEnabled('addin-exp-awarenessNudge')
                            ) {
                                // Two flags for two treatments
                                // with and without customize action button on callout
                                window.requestAnimationFrame(() => {
                                    showUnpinnedAdminAddinFRECallout();
                                });
                            }
                        }
                    );
                });

            return () => {
                addinSetupPromise.then(() =>
                    deinitializeAddinsForCalendarFolder(composeFormId, hostItemIndex)
                );
            };
        }
    }, [composeFormId, hostItemIndex]);

    React.useEffect(() => {
        // initialize preferred room list if the room booking assistant flight is on
        if (
            isFeatureEnabled('cal-mf-roomBookingAssistant') &&
            !isConnectedAccount(
                getComposeEvent(composeFormId).ParentFolderId.mailboxInfo.userIdentity
            )
        ) {
            lazyInitRoomPreferencesSetting.importAndExecute();
        }
    }, [composeFormId]);

    React.useEffect(() => {
        // initialize mailtips for already present recipients
        initializeMailTips();
    }, []);

    React.useEffect(
        () => () => {
            lazyOnComposeClose
                .import()
                .then(onComposeClose => {
                    onComposeClose(hostItemIndex);
                    return lazyDeinitializeAddinsForItem.import();
                })
                .then(deinitializeAddinsForItem => {
                    deinitializeAddinsForItem(hostItemIndex);
                });
        },
        []
    );

    const verifyAndSave = React.useCallback(() => {
        const fullComposeViewState = getFullComposeViewState(composeFormId);
        updateContentToViewState(fullComposeViewState?.bodyViewState);

        verifyEventThenSave();
    }, [composeFormId]);

    useGlobalHotkey(getCommands().saveAppointment, verifyAndSave, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => {
            return (
                !getFullComposeViewState(composeFormId).showSchedulingAssistant &&
                !isEventAMeeting(getComposeEvent(composeFormId))
            );
        },
    });
    useGlobalHotkey(getCommands().sendMeeting, verifyAndSave, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => {
            return (
                !getFullComposeViewState(composeFormId).showSchedulingAssistant &&
                isEventAMeeting(getComposeEvent(composeFormId))
            );
        },
    });

    const onDismissContextMenu = React.useCallback((): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            window.requestAnimationFrame(() => {
                if (leftPane.current) {
                    leftPane.current.focusToCharmPicker();
                }
                return resolve();
            });
        });
    }, []);

    if (!event || !fullComposeViewState) {
        return null;
    }

    const commandsRowStyle = { backgroundColor: calendarColorScheme.calLighterAlt };
    const isBarExp =
        shouldShowFormHeader(composeFormId) &&
        isHostAppFeatureEnabled('reduceCalBarExp') &&
        isFeatureEnabled('cal-mf-reduceCalendarBar');
    const buttonProps = getButtonProps(
        calendarColorScheme,
        showFullScreen,
        fullComposeViewState.showSchedulingAssistant
    );

    return (
        <FullComposeDroppableContainer formId={composeFormId}>
            <div
                className={classNames(styles.container, styles.contentWidthLimits, {
                    showFullScreen: showFullScreen,
                    disableDarkMode:
                        !fullComposeViewState.showSchedulingAssistant &&
                        !fullComposeViewState.isDarkMode,
                })}>
                <FullComposeHeader formId={composeFormId} showFullScreen={showFullScreen} />
                {fullComposeViewState.showSchedulingAssistant ? (
                    <FullComposeSchedulingAssistant
                        className={!showFullScreen ? styles.contentWidthLimits : ''}
                        formId={composeFormId}
                    />
                ) : (
                    <>
                        {fullComposeViewState.awaitingServiceResponse ? (
                            <Spinner
                                size={SpinnerSize.small}
                                theme={themeFromColorScheme(calendarColorScheme)}
                                className={styles.spinner}
                            />
                        ) : (
                            <div
                                className={classNames({ [styles.commandsRow]: isBarExp })}
                                style={isBarExp ? commandsRowStyle : null}>
                                <div className={classNames({ [styles.commandBar]: isBarExp })}>
                                    <FormsCommandBar
                                        filterableItems={getComposeCommandBarItems(
                                            event,
                                            originalEvent,
                                            fullComposeViewState,
                                            onDismissContextMenu,
                                            calendarColorScheme,
                                            !fullComposeViewState.uiEnabled /* disabled */
                                        )}
                                        colorScheme={calendarColorScheme}
                                    />
                                </div>
                                {isBarExp && (
                                    <CalendarBarButtons
                                        className={styles.calendarButtons}
                                        buttonProps={buttonProps}
                                        colorScheme={calendarColorScheme}
                                    />
                                )}
                            </div>
                        )}

                        <FullComposeInfoBar
                            colorScheme={calendarColorScheme}
                            formId={composeFormId}
                        />

                        <div
                            className={classNames(styles.contentPane, styles.contentWidthLimits, {
                                showFullScreen: showFullScreen,
                            })}>
                            <FullComposeLeftPane
                                className={classNames(styles.column, styles.contentColumn)}
                                formId={composeFormId}
                                ref={leftPane}
                            />
                            <FullComposeRightPane
                                className={classNames(styles.column, styles.schedulingColumn)}
                                formId={composeFormId}
                            />
                        </div>
                        <FullComposeSidePane formId={composeFormId} />
                        <MeetingResponseDialogContainer
                            itemId={event.ItemId}
                            colorScheme={calendarColorScheme}
                        />
                    </>
                )}
            </div>
        </FullComposeDroppableContainer>
    );
});

/**
 * Deintializes addin for the given folder
 */
function deinitializeAddinsForCalendarFolder(formId: string | null, hostItemId: string) {
    const fullComposeViewState = getFullComposeViewState(formId);

    if (
        fullComposeViewState?.currentSidePaneType ===
        SidePaneType.Addins /*Check if addin sidepane is active*/
    ) {
        updateSidePaneType(formId, SidePaneType.None);
    }

    lazyDeinitializeAddinsForItem.import().then(deinitializeAddinsForItem => {
        deinitializeAddinsForItem(hostItemId);
    });
}

function showLatestNewAdminAddinFRECallout() {
    let newAddinId = getSurfaceNewAddinId(ExtensibilityModeEnum.AppointmentAttendee);
    if (newAddinId) {
        let newAddinName: string = 'New Add-in';
        const extension = getExtensionForAddinId(newAddinId);
        if (extension) {
            newAddinName = extension.DisplayName;
        }

        lazyShowAdminInstalledNewAddinFRECallout.importAndExecute(
            ExtensibilityModeEnum.AppointmentOrganizer,
            newAddinId,
            newAddinName
        );
    }
}

function showUnpinnedAdminAddinFRECallout() {
    lazyShowUnpinnedAdminAddinFRECallout.importAndExecute(
        ExtensibilityModeEnum.AppointmentAttendee,
        // If the below flag is enabled, customise action button won't be shown in callout
        isFeatureEnabled('addin-exp-awarenessNudge'),
        CALENDAR_ELLIPSES_ID
    );
}
